"use client";

import type { EntityPermissions } from "@/modules/conversation/types";
import { useTranslation } from "@/lib/i18n";

const permissionKeys = [
  "can_send_messages",
  "can_send_media",
  "can_send_files",
  "can_send_voice_notes",
  "can_send_links",
  "can_create_polls",
  "can_change_info",
  "can_invite_users",
  "can_pin_messages",
  "can_start_calls",
] as const;

const defaultPermissions: EntityPermissions = {
  can_send_messages: true,
  can_send_media: true,
  can_send_files: true,
  can_send_voice_notes: true,
  can_send_links: true,
  can_create_polls: false,
  can_change_info: false,
  can_invite_users: false,
  can_pin_messages: false,
  can_start_calls: true,
};

interface Props {
  title: string;
  hint: string;
  value: EntityPermissions | null | undefined;
  onChange: (next: EntityPermissions) => void;
  disabled?: boolean;
}

export function normalizePermissions(value: EntityPermissions | null | undefined): EntityPermissions {
  return {
    ...defaultPermissions,
    ...(value ?? {}),
  };
}

export function PermissionSettings({ title, hint, value, onChange, disabled = false }: Props) {
  const t = useTranslation();
  const normalized = normalizePermissions(value);

  function updatePermission(key: keyof EntityPermissions, checked: boolean) {
    onChange({
      ...normalized,
      [key]: checked,
    });
  }

  return (
    <div className="rounded-2xl border border-m-divider bg-m-surface-light/40 p-4">
      <div>
        <p className="text-sm font-semibold text-m-text">{title}</p>
        <p className="mt-1 text-xs text-m-text-secondary">{hint}</p>
      </div>
      <div className="mt-4 grid gap-2">
        {permissionKeys.map((key) => (
          <label
            key={key}
            className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 ${
              disabled
                ? "border-m-divider bg-m-surface/60 text-m-text-hint"
                : "border-m-divider bg-m-surface text-m-text"
            }`}
          >
            <span className="text-sm">{t(`permissions.${key}`)}</span>
            <input
              type="checkbox"
              checked={normalized[key]}
              onChange={(e) => updatePermission(key, e.target.checked)}
              disabled={disabled}
              aria-label={t(`permissions.${key}`)}
            />
          </label>
        ))}
      </div>
    </div>
  );
}
